import axios from 'axios'
import { AxiosResponse, InternalAxiosRequestConfig } from './types'
import { ElMessage } from 'element-plus'
import qs from 'qs'
import { SUCCESS_CODE, LOGIN_EXPIRE_CODE, TRANSFORM_REQUEST_DATA } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'
import { objToFormData } from '@/utils'
import { refreshTokenApi } from '@/api/login'

// 刷新 token 的 Promise 单例，防止并发刷新
let refreshPromise: Promise<string | null> | null = null

const defaultRequestInterceptors = (config: InternalAxiosRequestConfig) => {
  if (
    config.method === 'post' &&
    config.headers['Content-Type'] === 'application/x-www-form-urlencoded'
  ) {
    config.data = qs.stringify(config.data)
  } else if (
    TRANSFORM_REQUEST_DATA &&
    config.method === 'post' &&
    config.headers['Content-Type'] === 'multipart/form-data'
  ) {
    config.data = objToFormData(config.data)
  }
  if (config.method === 'get' && config.params) {
    let url = config.url as string
    url += '?'
    const keys = Object.keys(config.params)
    for (const key of keys) {
      if (config.params[key] !== void 0 && config.params[key] !== null) {
        url += `${key}=${encodeURIComponent(config.params[key])}&`
      }
    }
    url = url.substring(0, url.length - 1)
    config.params = {}
    config.url = url
  }
  return config
}

/**
 * 执行 token 刷新
 * 使用单例 Promise 确保并发请求只触发一次刷新
 */
const doRefreshToken = (
  userStore: ReturnType<typeof useUserStoreWithOut>
): Promise<string | null> => {
  if (refreshPromise) {
    return refreshPromise
  }

  const refreshToken = userStore.getRefreshToken
  if (!refreshToken) {
    return Promise.resolve(null)
  }

  refreshPromise = refreshTokenApi(refreshToken)
    .then((res) => {
      const resCode = (res as any)?.code ?? (res as any)?.data?.code
      const resData = (res as any)?.data?.data ?? (res as any)?.data
      if (resCode == SUCCESS_CODE && resData) {
        const tokenType = resData.tokenType || 'Bearer'
        const accessToken = resData.accessToken
        const newRefreshToken = resData.refreshToken
        if (accessToken) {
          userStore.setToken(`${tokenType} ${accessToken}`)
        }
        if (newRefreshToken) {
          userStore.setRefreshToken(newRefreshToken)
        }
        return userStore.getToken
      }
      return null
    })
    .catch(() => {
      // 刷新失败，返回 null
      return null
    })
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

const defaultResponseInterceptors = async (response: AxiosResponse) => {
  if (response?.config?.responseType === 'blob') {
    // 如果是文件流，直接过
    return response
  }

  if (response.data.code == SUCCESS_CODE) {
    return response.data
  }

  const userStore = useUserStoreWithOut()
  const config = response.config as any
  const isRefreshRequest = String(config?.url || '').includes('/auth/refresh')
  const isTokenExpired = response?.data?.code === 401 || response.data.code === LOGIN_EXPIRE_CODE

  // 刷新请求本身失败，直接返回错误，不显示消息，让调用方处理
  if (isRefreshRequest) {
    return Promise.reject(response.data)
  }

  // Token 过期且未重试过，尝试刷新
  if (isTokenExpired && !config?._retry) {
    const refreshToken = userStore.getRefreshToken
    if (!refreshToken) {
      userStore.logout()
      return Promise.reject(response.data)
    }

    // 执行刷新
    const newToken = await doRefreshToken(userStore)

    if (!newToken) {
      // 刷新失败，登出
      userStore.logout()
      return Promise.reject(response.data)
    }

    // 刷新成功，重试原请求
    config._retry = true
    config.headers = {
      ...(config.headers || {}),
      [userStore.getTokenKey ?? 'Authorization']: newToken
    }
    const retryResponse = await axios.request(config)
    return defaultResponseInterceptors(retryResponse)
  }

  // 其他错误情况，显示错误消息
  ElMessage.error(response?.data?.msg || response?.data?.message || '请求失败')

  // 如果是 token 过期错误且已经重试过，说明刷新也失败了，需要登出
  if (isTokenExpired) {
    userStore.logout()
  }

  return Promise.reject(response.data)
}

export { defaultResponseInterceptors, defaultRequestInterceptors }
